import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DayCard from './components/DayCard';
import ActivityModal from './components/ActivityModal';
import AccommodationModal from './components/AccommodationModal';
import ShareModal from './components/ShareModal';
import CostSummary from './components/CostSummary';
import ExpenseModal from './components/ExpenseModal';
import GroupMembersModal from './components/GroupMembersModal';
import { createTripGroup, getTripGroups, subscribeToGroupChanges, unsubscribeFromGroupChanges } from '../../services/groupService';
import { useAuth } from '../../contexts/AuthContext';
import GroupChatModal from './components/GroupChatModal';

const ItineraryManagement = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isAccommodationModalOpen, setIsAccommodationModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  const [tripData, setTripData] = useState({
    id: 1,
    destination: 'Boracay Island Paradise',
    description: '5-day beach getaway with water sports and island hopping',
    image: 'https://images.unsplash.com/photo-1722223072163-5b737b45ca2b',
    imageAlt: 'Aerial view of pristine white sand beach with crystal clear turquoise water and traditional Filipino boats in Boracay Island',
    startDate: '2026-03-15',
    endDate: '2026-03-20',
    travelers: 2,
    status: 'upcoming'
  });

  const [itinerary, setItinerary] = useState([
    {
      day: 1,
      date: '2026-03-15',
      title: 'Arrival & Beach Exploration',
      activities: [
        { id: 1, time: '10:00 AM', title: 'Airport Pickup', description: 'Meet at Caticlan Airport', cost: 500, category: 'transport' },
        { id: 2, time: '02:00 PM', title: 'White Beach Walk', description: 'Explore the famous white sand beach', cost: 0, category: 'activity' },
        { id: 3, time: '07:00 PM', title: 'Sunset Dinner', description: 'Beachfront dining at D\'Talipapa', cost: 1200, category: 'food' }
      ],
      accommodation: { name: 'Boracay Beach Resort', checkIn: '02:00 PM', checkOut: '12:00 PM', cost: 3500, address: 'Station 2, White Beach' }
    },
    {
      day: 2,
      date: '2026-03-16',
      title: 'Island Hopping Adventure',
      activities: [
        { id: 4, time: '08:00 AM', title: 'Island Hopping Tour', description: 'Visit Crystal Cove and Crocodile Island', cost: 1500, category: 'activity' },
        { id: 5, time: '01:00 PM', title: 'Lunch at Puka Beach', description: 'Fresh seafood lunch', cost: 800, category: 'food' },
        { id: 6, time: '04:00 PM', title: 'Snorkeling Session', description: 'Explore coral reefs', cost: 500, category: 'activity' }
      ],
      accommodation: { name: 'Boracay Beach Resort', checkIn: '02:00 PM', checkOut: '12:00 PM', cost: 3500, address: 'Station 2, White Beach' }
    },
    {
      day: 3,
      date: '2026-03-17',
      title: 'Water Sports & Relaxation',
      activities: [
        { id: 7, time: '09:00 AM', title: 'Parasailing', description: 'Aerial view of the island', cost: 2000, category: 'activity' },
        { id: 8, time: '11:00 AM', title: 'Jet Ski Rental', description: '30-minute jet ski adventure', cost: 1500, category: 'activity' },
        { id: 9, time: '06:00 PM', title: 'Beach Massage', description: 'Relaxing massage by the beach', cost: 600, category: 'wellness' }
      ],
      accommodation: { name: 'Boracay Beach Resort', checkIn: '02:00 PM', checkOut: '12:00 PM', cost: 3500, address: 'Station 2, White Beach' }
    },
    {
      day: 4,
      date: '2026-03-18',
      title: 'Cultural Experience',
      activities: [
        { id: 10, time: '10:00 AM', title: 'Mt. Luho Viewpoint', description: 'Highest point in Boracay', cost: 200, category: 'sightseeing' },
        { id: 11, time: '02:00 PM', title: 'Local Market Tour', description: 'D\'Mall shopping and local crafts', cost: 1000, category: 'shopping' },
        { id: 12, time: '08:00 PM', title: 'Fire Dance Show', description: 'Traditional fire dancing performance', cost: 0, category: 'entertainment' }
      ],
      accommodation: { name: 'Boracay Beach Resort', checkIn: '02:00 PM', checkOut: '12:00 PM', cost: 3500, address: 'Station 2, White Beach' }
    },
    {
      day: 5,
      date: '2026-03-20',
      title: 'Departure Day',
      activities: [
        { id: 13, time: '08:00 AM', title: 'Breakfast at Hotel', description: 'Final meal with ocean view', cost: 500, category: 'food' },
        { id: 14, time: '11:00 AM', title: 'Check-out & Airport Transfer', description: 'Transfer to Caticlan Airport', cost: 500, category: 'transport' }
      ],
      accommodation: null
    }
  ]);

  // Load or create group for this trip
  useEffect(() => {
    const initializeGroup = async () => {
      if (!tripId || !user?.id) return;

      const groups = await getTripGroups(tripId);
      if (groups?.length > 0) {
        setCurrentGroup(groups?.[0]);
        setGroupMembers(groups?.[0]?.members || []);
      } else {
        // Create default group for this trip
        const result = await createTripGroup(tripId, {
          name: `${tripData?.destination} Group`,
          description: 'Collaborative travel planning group'
        }, user?.id);
        
        if (result?.success) {
          setCurrentGroup(result?.group);
          setGroupMembers([{ userId: user?.id, role: 'owner' }]);
        }
      }
    };

    initializeGroup();
  }, [tripId, user?.id]);

  // Subscribe to real-time group changes
  useEffect(() => {
    if (!currentGroup?.id) return;

    const channel = subscribeToGroupChanges(currentGroup?.id, (update) => {
      console.log('Group update received:', update);
      // Reload group data on changes
      if (update?.type === 'member') {
        loadGroupMembers();
      }
    });

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        unsubscribeFromGroupChanges(channel);
      }
    };
  }, [currentGroup?.id]);

  const loadGroupMembers = async () => {
    if (!currentGroup?.id) return;
    const groups = await getTripGroups(tripId);
    if (groups?.length > 0) {
      setGroupMembers(groups?.[0]?.members || []);
    }
  };

  const handleViewSplits = () => {
    setIsExpenseModalOpen(true);
  };

  const handleAddActivity = (dayNumber) => {
    setSelectedDay(dayNumber);
    setEditingItem(null);
    setIsActivityModalOpen(true);
  };

  const handleEditActivity = (dayNumber, activity) => {
    setSelectedDay(dayNumber);
    setEditingItem(activity);
    setIsActivityModalOpen(true);
  };

  const handleDeleteActivity = (dayNumber, activityId) => {
    setItinerary(prev => prev?.map(day => 
      day?.day === dayNumber 
        ? { ...day, activities: day?.activities?.filter(a => a?.id !== activityId) }
        : day
    ));
  };

  const handleSaveActivity = (dayNumber, activityData) => {
    setItinerary(prev => prev?.map(day => {
      if (day?.day === dayNumber) {
        if (editingItem) {
          return {
            ...day,
            activities: day?.activities?.map(a => 
              a?.id === editingItem?.id ? { ...a, ...activityData } : a
            )
          };
        } else {
          const newActivity = {
            ...activityData,
            id: Date.now()
          };
          return {
            ...day,
            activities: [...(day?.activities || []), newActivity]
          };
        }
      }
      return day;
    }));
    setIsActivityModalOpen(false);
  };

  const handleEditAccommodation = (dayNumber, accommodation) => {
    setSelectedDay(dayNumber);
    setEditingItem(accommodation);
    setIsAccommodationModalOpen(true);
  };

  const handleSaveAccommodation = (dayNumber, accommodationData) => {
    setItinerary(prev => prev?.map(day => 
      day?.day === dayNumber 
        ? { ...day, accommodation: accommodationData }
        : day
    ));
    setIsAccommodationModalOpen(false);
  };

  const calculateTotalCost = () => {
    let total = 0;
    itinerary?.forEach(day => {
      day?.activities?.forEach(activity => {
        total += activity?.cost || 0;
      });
      if (day?.accommodation) {
        total += day?.accommodation?.cost || 0;
      }
    });
    return total;
  };

  const getCostBreakdown = () => {
    const breakdown = {
      activities: 0,
      accommodation: 0,
      food: 0,
      transport: 0,
      other: 0
    };

    itinerary?.forEach(day => {
      day?.activities?.forEach(activity => {
        const cost = activity?.cost || 0;
        if (activity?.category === 'food') {
          breakdown.food += cost;
        } else if (activity?.category === 'transport') {
          breakdown.transport += cost;
        } else {
          breakdown.activities += cost;
        }
      });
      if (day?.accommodation) {
        breakdown.accommodation += day?.accommodation?.cost || 0;
      }
    });

    return breakdown;
  };

  const handleOpenChat = () => {
    if (!currentGroup) {
      alert('Please create a group first before opening chat');
      return;
    }
    setIsChatModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-20">
        {/* Trip Header */}
        <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <button
                  onClick={() => navigate('/user-dashboard')}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
                >
                  <Icon name="ArrowLeft" size={16} />
                  Back to Dashboard
                </button>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  {tripData?.destination}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mb-4">
                  {tripData?.description}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} className="text-primary" />
                    <span className="text-foreground font-medium">
                      {new Date(tripData?.startDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' - '}
                      {new Date(tripData?.endDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={16} className="text-secondary" />
                    <span className="text-foreground font-medium">{tripData?.travelers} Travelers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} className="text-accent" />
                    <span className="text-foreground font-medium">{itinerary?.length} Days</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => setIsMembersModalOpen(true)}
                  iconName="Users"
                  iconPosition="left"
                  disabled={!currentGroup}
                >
                  Members ({groupMembers?.length})
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleOpenChat}
                  iconName="MessageCircle"
                  iconPosition="left"
                  disabled={!currentGroup}
                >
                  Group Chat
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => setIsExpenseModalOpen(true)}
                  iconName="DollarSign"
                  iconPosition="left"
                >
                  Expenses
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => setIsShareModalOpen(true)}
                  iconName="Share2"
                  iconPosition="left"
                >
                  Share
                </Button>
                <Button
                  variant="default"
                  size="default"
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Itinerary Timeline */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground">Day-by-Day Itinerary</h2>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Day
                </Button>
              </div>

              {itinerary?.map((day, index) => (
                <DayCard
                  key={day?.day}
                  day={day}
                  isLast={index === itinerary?.length - 1}
                  onAddActivity={handleAddActivity}
                  onEditActivity={handleEditActivity}
                  onDeleteActivity={handleDeleteActivity}
                  onEditAccommodation={handleEditAccommodation}
                />
              ))}
            </div>

            {/* Cost Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <CostSummary
                  totalCost={calculateTotalCost()}
                  breakdown={getCostBreakdown()}
                  travelers={tripData?.travelers}
                  groupId={currentGroup?.id}
                  onViewSplits={handleViewSplits}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
        dayNumber={selectedDay}
        editingActivity={editingItem}
        onSave={handleSaveActivity}
      />

      <AccommodationModal
        isOpen={isAccommodationModalOpen}
        onClose={() => setIsAccommodationModalOpen(false)}
        dayNumber={selectedDay}
        editingAccommodation={editingItem}
        onSave={handleSaveAccommodation}
      />

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        tripData={tripData}
        groupId={currentGroup?.id}
      />

      <ExpenseModal
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        groupId={currentGroup?.id}
        groupMembers={groupMembers}
      />

      <GroupMembersModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        groupId={currentGroup?.id}
      />

      <GroupChatModal
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        groupId={currentGroup?.id}
        groupName={currentGroup?.name}
      />
    </div>
  );
};

export default ItineraryManagement;